import { api } from "./index";

/** Prefer adminToken when explicitly requested or when on /admin/* pages. */
function shouldUseAdminAuth(options = {}) {
  if (options.useAdminAuth === true) return true;
  if (typeof window === "undefined") return false;
  return String(window.location?.pathname || "").startsWith("/admin");
}

export async function fetchTrackHubBranches(params = {}, options = {}) {
  const response = await api.get("/trackhub/branches", {
    params,
    useAdminAuth: shouldUseAdminAuth(options),
  });
  return response.data;
}

export async function patchTrackHubBranch(branchId, payload, options = {}) {
  const response = await api.patch(`/trackhub/branches/${branchId}`, payload, {
    useAdminAuth: shouldUseAdminAuth(options),
  });
  return response.data;
}

export async function bulkUpdateTrackHubBranches(payload, options = {}) {
  const response = await api.patch("/trackhub/branches/bulk", payload, {
    useAdminAuth: shouldUseAdminAuth(options),
  });
  return response.data;
}

export async function fetchTrackHubReports(params = {}, options = {}) {
  const response = await api.get("/trackhub/reports", {
    params,
    useAdminAuth: shouldUseAdminAuth(options),
  });
  return response.data;
}

export async function fetchTrackHubReportsSummary(params = {}, options = {}) {
  const response = await api.get("/trackhub/reports/summary", {
    params,
    useAdminAuth: shouldUseAdminAuth(options),
  });
  return response.data;
}

export async function fetchShipmentReports(params = {}, options = {}) {
  const response = await api.get("/shipment-reports", {
    params,
    useAdminAuth: shouldUseAdminAuth(options),
  });
  return response.data;
}

export async function fetchShipmentReportsExport(params = {}, options = {}) {
  const response = await api.get("/shipment-reports/export", {
    params,
    responseType: "blob",
    useAdminAuth: shouldUseAdminAuth(options),
  });
  let filename = `shipment-reports-${new Date().toISOString().slice(0, 10)}.csv`;
  const cd = response.headers && response.headers["content-disposition"];
  if (cd) {
    const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(cd);
    if (match && match[1]) filename = match[1].replace(/['"]/g, "");
  }
  return { blob: response.data, filename };
}

export async function fetchTrackHubReportsExport(params = {}, options = {}) {
  const response = await api.get("/trackhub/reports/export", {
    params,
    responseType: "blob",
    useAdminAuth: shouldUseAdminAuth(options),
  });
  let filename = `trackhub-shipments-${new Date().toISOString().slice(0, 10)}.csv`;
  const cd = response.headers && response.headers["content-disposition"];
  if (cd) {
    const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(cd);
    if (match && match[1]) filename = match[1].replace(/['"]/g, "");
  }
  return { blob: response.data, filename };
}

export async function quoteTrackHubShipment(payload, options = {}) {
  const response = await api.post("/trackhub/shipments/quote", payload, {
    useAdminAuth: shouldUseAdminAuth(options),
  });
  return response.data;
}

export async function resolveTrackHubDropoff(payload, options = {}) {
  const response = await api.post("/trackhub/shipments/resolve-dropoff", payload, {
    useAdminAuth: shouldUseAdminAuth(options),
  });
  return response.data;
}

export async function resolveTrackHubLocation(payload, options = {}) {
  const response = await api.post("/trackhub/shipments/resolve-location", payload, {
    useAdminAuth: shouldUseAdminAuth(options),
  });
  return response.data;
}

export async function reverseTrackHubLocation(payload, options = {}) {
  const response = await api.post("/trackhub/shipments/reverse-location", payload, {
    useAdminAuth: shouldUseAdminAuth(options),
  });
  return response.data;
}

export async function createManualTrackHubShipment(payload, options = {}) {
  const response = await api.post("/trackhub/shipments/manual", payload, {
    useAdminAuth: shouldUseAdminAuth(options),
  });
  return response.data;
}

export async function createTrackHubShipment(payload, options = {}) {
  const response = await api.post("/trackhub/shipments", payload, {
    useAdminAuth: shouldUseAdminAuth(options),
  });
  return response.data;
}

export async function downloadShipmentDocument(reportId, kind, lang, options = {}) {
  const response = await api.get(
    `/trackhub/reports/${encodeURIComponent(reportId)}/${kind}`,
    {
      params: { lang },
      responseType: "blob",
      useAdminAuth: shouldUseAdminAuth(options),
    }
  );
  let filename = options.filename || `${kind}.pdf`;
  const cd = response.headers && response.headers["content-disposition"];
  if (cd) {
    const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(cd);
    if (match && match[1]) filename = match[1].replace(/['"]/g, "");
  }
  return { blob: response.data, filename };
}

export async function cancelTrackHubShipment(shipmentId, options = {}) {
  const response = await api.post(
    `/trackhub/shipments/${encodeURIComponent(shipmentId)}/cancel`,
    {},
    {
      useAdminAuth: shouldUseAdminAuth(options),
    }
  );
  return response.data;
}

export async function fetchTrackHubCarriers(params = {}, options = {}) {
  const response = await api.get("/trackhub/carriers", {
    params: {
      refresh: true,
      ...params,
      _ts: Date.now(),
    },
    useAdminAuth: shouldUseAdminAuth(options),
  });
  return response.data;
}

export async function patchTrackHubCarrier(carrierId, payload, options = {}) {
  const response = await api.patch(`/trackhub/carriers/${encodeURIComponent(carrierId)}`, payload, {
    useAdminAuth: shouldUseAdminAuth(options),
  });
  return response.data;
}

export async function fetchDeliveryCostConfig(params = {}, options = {}) {
  const response = await api.get("/trackhub/delivery-cost-config", {
    params,
    useAdminAuth: shouldUseAdminAuth(options),
  });
  return response.data;
}

export async function putDeliveryCostConfig(payload, options = {}) {
  const response = await api.put("/trackhub/delivery-cost-config", payload, {
    useAdminAuth: shouldUseAdminAuth(options),
  });
  return response.data;
}

export async function previewDeliveryCostConfig(payload, options = {}) {
  const response = await api.post("/trackhub/delivery-cost-config/preview", payload, {
    useAdminAuth: shouldUseAdminAuth(options),
  });
  return response.data;
}
